import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, NgZone, ChangeDetectionStrategy } from '@angular/core';
import * as MediumEditor from 'medium-editor';
import {ApiService} from '../shared/api.service';

@Component({
    selector: 'richtext',
    template: '<textarea #textarea></textarea>',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RichtextComponent implements OnInit, OnDestroy {
    public id: string = '';
    private editor;
    private heartbeatSubscription;
    private _value: string|Array<{text: string}> = '';

    @Input()
    get value() {
        return this._value;
    }
    set value(value) {
        if( !value ) {
            value = '';
        }
        if( this.editor && !this.working && !this.isFocused() ) {
            // we need to update medium editor when we are not updating ourself
            value = this.getValue(value);
            this.editor.setContent(value);
        }
        this._value = value;
    }
    @Output() valueChange = new EventEmitter();

    @Output() delete = new EventEmitter();
    @Output() enter = new EventEmitter();
    @Output() blur = new EventEmitter();

    options = {
        placeholder: {
            text: '',
            hideOnClick: true
        },
        paste:<any> {
            cleanPastedHTML: true,
            cleanReplacements: [
                ['“', '"'],
                ['”', '"'],
                ['’', "'"],
                ['‘', "'"],
                [/\n|\r/, ' '],
                [/<(h[1-6]|div|p|section|ol|li|table)[^>]*>/gi, ''],
                [/<\/(h[1-6]|div|p|section|ol|li|table)>/gi, '']
            ],
            cleanAttrs: [
                'class', 
                'style',
                'size', 
                'align',
                'id',
                'dir'
            ],
            cleanTags: [
                'meta',
                'style',
                'script',
                'center',
                'basefont',
                'font',
                'span',
                'code',
                'pre',
                'br',
                'table',
                'thead',
                'th',
                'tbody',
                'tr',
                'td'
            ]
        },
        disableReturn: true,
        disableDoubleReturn: true,
        disableEditing: false,
        targetBlank: true,
        imageDragging: false,
        toolbar:<any> {
            buttons: [
                {
                    name: 'bold',
                    contentDefault: '<i class="material-icons">format_bold</i>'
                },
                {
                    name: 'italic',
                    contentDefault: '<i class="material-icons">format_italic</i>'
                },
                {
                    name: 'underline',
                    contentDefault: '<i class="material-icons">format_underline</i>'
                },
                {
                    name: 'anchor',
                    contentDefault: '<i class="material-icons">link</i>'
                }
            ]
        }
    };
    
    @Input() enableReturn;
    @Input() dialog;
    @Input() placeholder;

    private _boldButton = true;
    @Input()
    set boldButton(boldButton) {
        this._boldButton = boldButton;
    }
    get boldButton() {
        return this._boldButton;
    }
    private _removeButtons = false;
    @Input()
    set removeButtons(removeButtons) {
        this._removeButtons = removeButtons;
    }
    get removeButtons() {
        return this._removeButtons;
    }

    @ViewChild('textarea', {static: true}) textarea;

    constructor(private api: ApiService, private zone: NgZone) {}
    
    ngOnInit() {
        if( this.enableReturn === true ) {
            this.options.disableDoubleReturn = false;
            this.options.disableReturn = false;
            this.options.paste.cleanReplacements = [
                ['“', '"'],
                ['”', '"'],
                ['’', "'"],
                ['‘', "'"],
                [/\n|\r/, ' '],
                [/<(ol|table)[^>]*>/gi, ''],
                [/<\/(ol|table)>/gi, ''],
                [/<(h[1-6]|div|section|li)[^>]*>/gi, '<p>'],
                [/<\/(h[1-6]|div|section|li)>/gi, '</p>']
            ];
        }
        if( this.placeholder ) {
            this.options.placeholder.text = this.placeholder;
        }
        if( !this.boldButton ) {
            this.options.toolbar.buttons = this.options.toolbar.buttons.filter(item => item.name !== 'bold');
        }
        if( this.removeButtons === true ) {
            this.options.toolbar = false;
            this.options.paste.unwrapTags = [
                'i',
                'a',
                'b',
                'u'
            ];
        }
        let el = this.textarea.nativeElement;
        this.zone.runOutsideAngular(() => {
            this.editor = new MediumEditor(el, this.options);
            this.id = this.editor.id;
            this.value = this.getValue(this.value);
            this.editor.setContent(this.value);
            this.updateModel = this.updateModel.bind(this);
            this.editor.subscribe('editableInput', this.updateModel);
            this.editor.subscribe('editableKeydownEnter', (event) => {
                this.enter.emit(event);
            });
            this.editor.subscribe('editableKeydownDelete', (event) => {
                this.delete.emit(event);
            });
            this.editor.subscribe('blur', (event) => {
                if (event.srcElement && event.srcElement.innerText === '%ME_PASTEBIN%') {
                    // Blur event was dispatched due to pasting
                    return;
                }
                this.updateModel();
                this.blur.emit(this.value);
            });
        });
        
        if( this.dialog === true && this.removeButtons === false ) {
            const id = this.editor.id;
            const toolbar = document.getElementById('medium-editor-toolbar-' + id);
            if( toolbar.id ) {
                toolbar.classList.add('fixed');
            }
        }
        this.heartbeatSubscription = this.api.heartbeat.subscribe(() => {
            this.updateModel();
        });
    }
    getValue(value) {
        if( Array.isArray(value) ) {
            value = value.reduce((out, item) => out += '<p>' + this.mark(item.text) + '</p>', '');
        }
        return this.mark(value);
    }
    isFocused() {
        if( !this.editor ) {
            return false;
        }
        return document.activeElement === this.editor.elements[0];
    }
    /**
     * Mark banned words
     */
    mark(text) {
        const blacklist = this.api.getProp('blacklist');
        const len = blacklist.length;
        text = text.replace(/<(?:mark|span)[^>]*>|<\/(?:mark|span)>/gi, '');
        for(var i = 0; i < len; i++) {
            const term = blacklist[i];
            const regex = new RegExp('\\b(' + term + ')\\b(?!<\\/mark>)', 'gi');
            text = text.replace(regex, '<mark>$1</mark>');
        }
        this.working = false;
        return text;
    }

    /**
     * Emit updated model
     */
    working = false;
    updateModel() {
        if( this.working ) {
            return;
        }
        this.zone.run(() => {
            this.working = true;
            const value = this.editor.getContent();
            const cleanValue = this.stripTags(value);
            this.value = cleanValue;
            this.valueChange.emit(this.value);
            const el = this.editor.elements[0];
            const marked = this.mark(el.innerHTML);
            if( marked === value ) {
                return;
            }
            const selection = this.editor.exportSelection();
            el.innerHTML = this.mark(el.innerHTML);
            if( this.isFocused() ) {
                this.editor.importSelection(selection);
            }
        });        
    }
    stripTags(content) {
        if( this.enableReturn !== true ) {
            content = content.replace(/<p[^>]*>|<\/p>/gi, '');
        }
        content = content.replace(/<p[^>]*><\/p>/gi, '');
        content = content.replace(/<br>/gi, '');
        content = content.replace(/&nbsp;/gi, ' ');

        // grammarly fixes - grammarly doesn't trigger paste
        content = content.replace(/(\s|&nbsp;)<\/([abi])>/gi, ' </$2>');
        content = content.replace(/<([bi])[^>]*>(\s|&nbsp;)/gi, '<$1> ');
        content = content.replace(/<([a][^>]*)>(\s|&nbsp;)/gi, '<$1> ');
        content = content.replace(/<(?:mark|span)[^>]*>|<\/(?:mark|span)>/gi, '');

        if( this.removeButtons ) {
            content = content.replace(/<[aibu][^>]*>|<\/[aibu]>/gi, '');
        }
        return content;
    }

    /**
     * Remove MediumEditor on destruction of directive
     */
    ngOnDestroy(): void {
        if( this.editor ) {
            this.editor.destroy();
        }
        if( this.heartbeatSubscription ) {
            this.heartbeatSubscription.unsubscribe();
        }
    }
}